import { supabase } from '../lib/supabase';

// ==================== TYPES ====================

export interface SearchHistoryRecord {
    id: string;
    user_id: string;
    created_at: string;
    search_query: string;
    search_type: string;
    location: string | null;
    category: string | null;
    results_count: number;
    leads_saved: number;
    search_duration_ms: number;
    api_provider: string;
    api_credits_used: number;
    status: 'success' | 'error' | 'no_results';
    error_message: string | null;
}

export interface BulkMessageRecord {
    id: string;
    user_id: string;
    created_at: string;
    campaign_name: string | null;
    total_recipients: number;
    messages_sent: number;
    messages_failed: number;
    message_template: string | null;
    used_ai: boolean;
    started_at: string;
    completed_at: string | null;
    duration_seconds: number | null;
    avg_delay_seconds: number | null;
    whatsapp_instance: string | null;
    status: 'processing' | 'completed' | 'failed';
    error_message: string | null;
}

export interface TokenUsageRecord {
    id: string;
    user_id: string;
    created_at: string;
    usage_type: string;
    reference_id: string | null;
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    model_name: string;
    cost_usd: number;
    prompt_preview: string | null;
}

export interface OverviewMetrics {
    totalSearches: number;
    totalDispatches: number;
    totalTokens: number;
    totalCost: number;
    searchesChange?: number; // percentage change from previous period
    dispatchesChange?: number;
    tokensChange?: number;
    costChange?: number;
}

export interface DailyStats {
    date: string;
    searches: number;
    dispatches: number;
    messagesSent: number;
    messagesFailed: number;
    tokens: number;
    cost: number;
}

// ==================== ANALYTICS SERVICE ====================

/**
 * Fetch overview metrics for a specific period
 */
export async function fetchOverviewMetrics(period: 'today' | '7days' | '30days' = '30days'): Promise<OverviewMetrics> {
    const now = new Date();
    let startDate: Date;
    let previousStartDate: Date;

    switch (period) {
        case 'today':
            startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            previousStartDate = new Date(startDate);
            previousStartDate.setDate(previousStartDate.getDate() - 1);
            break;
        case '7days':
            startDate = new Date(now);
            startDate.setDate(startDate.getDate() - 7);
            previousStartDate = new Date(startDate);
            previousStartDate.setDate(previousStartDate.getDate() - 7);
            break;
        case '30days':
        default:
            startDate = new Date(now);
            startDate.setDate(startDate.getDate() - 30);
            previousStartDate = new Date(startDate);
            previousStartDate.setDate(previousStartDate.getDate() - 30);
            break;
    }

    // Current period
    const [searchResult, bulkResult, tokenResult] = await Promise.all([
        supabase
            .from('search_history')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', startDate.toISOString()),

        supabase
            .from('bulk_message_history')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', startDate.toISOString()),

        supabase
            .from('token_usage')
            .select('total_tokens, cost_usd')
            .gte('created_at', startDate.toISOString())
    ]);

    // Previous period for comparison
    const [prevSearchResult, prevBulkResult, prevTokenResult] = await Promise.all([
        supabase
            .from('search_history')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', previousStartDate.toISOString())
            .lt('created_at', startDate.toISOString()),

        supabase
            .from('bulk_message_history')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', previousStartDate.toISOString())
            .lt('created_at', startDate.toISOString()),

        supabase
            .from('token_usage')
            .select('total_tokens, cost_usd')
            .gte('created_at', previousStartDate.toISOString())
            .lt('created_at', startDate.toISOString())
    ]);

    const totalSearches = searchResult.count || 0;
    const totalDispatches = bulkResult.count || 0;
    const totalTokens = tokenResult.data?.reduce((sum, r) => sum + (r.total_tokens || 0), 0) || 0;
    const totalCost = tokenResult.data?.reduce((sum, r) => sum + (r.cost_usd || 0), 0) || 0;

    const prevSearches = prevSearchResult.count || 0;
    const prevDispatches = prevBulkResult.count || 0;
    const prevTokens = prevTokenResult.data?.reduce((sum, r) => sum + (r.total_tokens || 0), 0) || 0;
    const prevCost = prevTokenResult.data?.reduce((sum, r) => sum + (r.cost_usd || 0), 0) || 0;

    // Calculate percentage changes
    const calculateChange = (current: number, previous: number) => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return ((current - previous) / previous) * 100;
    };

    return {
        totalSearches,
        totalDispatches,
        totalTokens,
        totalCost,
        searchesChange: calculateChange(totalSearches, prevSearches),
        dispatchesChange: calculateChange(totalDispatches, prevDispatches),
        tokensChange: calculateChange(totalTokens, prevTokens),
        costChange: calculateChange(totalCost, prevCost),
    };
}

/**
 * Fetch search analytics with optional date filtering
 */
export async function fetchSearchAnalytics(startDate?: Date, endDate?: Date) {
    let query = supabase
        .from('search_history')
        .select('*')
        .order('created_at', { ascending: false });

    if (startDate) {
        query = query.gte('created_at', startDate.toISOString());
    }
    if (endDate) {
        query = query.lte('created_at', endDate.toISOString());
    }

    const { data, error } = await query.limit(100);

    if (error) throw error;
    return data as SearchHistoryRecord[];
}

/**
 * Fetch bulk message analytics with optional date filtering
 */
export async function fetchBulkMessageAnalytics(startDate?: Date, endDate?: Date) {
    let query = supabase
        .from('bulk_message_history')
        .select('*')
        .order('created_at', { ascending: false });

    if (startDate) {
        query = query.gte('created_at', startDate.toISOString());
    }
    if (endDate) {
        query = query.lte('created_at', endDate.toISOString());
    }

    const { data, error } = await query.limit(100);

    if (error) throw error;
    return data as BulkMessageRecord[];
}

/**
 * Fetch token usage analytics with optional date filtering
 */
export async function fetchTokenUsageAnalytics(startDate?: Date, endDate?: Date) {
    let query = supabase
        .from('token_usage')
        .select('*')
        .order('created_at', { ascending: false });

    if (startDate) {
        query = query.gte('created_at', startDate.toISOString());
    }
    if (endDate) {
        query = query.lte('created_at', endDate.toISOString());
    }

    const { data, error } = await query.limit(500);

    if (error) throw error;
    return data as TokenUsageRecord[];
}

/**
 * Fetch daily aggregated stats from the monthly_usage_stats VIEW
 */
export async function fetchDailyStats(startDate?: Date, endDate?: Date): Promise<DailyStats[]> {
    let query = supabase
        .from('monthly_usage_stats')
        .select('*')
        .order('date', { ascending: true });

    if (startDate) {
        query = query.gte('date', startDate.toISOString().split('T')[0]);
    }
    if (endDate) {
        query = query.lte('date', endDate.toISOString().split('T')[0]);
    }

    const { data, error } = await query;

    if (error) throw error;

    return (data || []).map(row => ({
        date: row.date,
        searches: row.searches || 0,
        dispatches: row.bulk_sends || 0,
        messagesSent: row.messages_sent || 0,
        messagesFailed: row.messages_failed || 0,
        tokens: row.tokens_used || 0,
        cost: row.cost_usd || 0,
    }));
}
